import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { useThemePerformance } from '@terminal/react-query-hooks/themes/useThemeOverview';
import { formatNumber } from '@terminal/views/dashboard/calendar-widget/calendar-event-drawer/drawer-content/utils/number-formatter';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import type { useThemePerformanceReturn } from '../hooks/useThemePerformanceReturn';
import styles from './HorizonItem.module.scss';

export type Horizon = '1D' | '1W' | '1M' | '3M' | '6M' | '1Y';

export interface HorizonItemProps {
  horizonToIndexMap: Map<string, number>;
  horizon: Horizon;
  isSelected: boolean;
  benchmarkTags?: string[];
  onClick: () => void;
  horizonLabelOverrides?: Partial<Record<Horizon, string>>;
  isPerformanceLoading?: boolean;
  horizonReturn?: number;
  getReturnValue: ReturnType<
    typeof useThemePerformanceReturn
  >['getReturnValue'];
}

export const HorizonItem = ({
  benchmarkTags,
  horizon,
  horizonToIndexMap,
  isSelected,
  onClick,
  horizonLabelOverrides,
  isPerformanceLoading,
  horizonReturn,
  getReturnValue,
}: HorizonItemProps) => {
  const { t } = useTranslation(['widget', 'scenario']);

  const horizonKey = horizon.toLowerCase();

  const { data: benchmarkData, isFetching: isBenchmarkFetching } =
    useThemePerformance(benchmarkTags, horizonToIndexMap.get(horizonKey));
  const benchmarkValue = benchmarkData?.aggregate?.at(-1)?.value;

  const returnValue = getReturnValue({
    value: horizonReturn ?? undefined,
    benchmarkValue,
    withBenchmark: !!benchmarkTags?.length,
  });

  const dataToUse = {
    value: formatNumber({ isPercentage: true, value: returnValue }),
    isNegative: !!returnValue && returnValue < 0,
    isLoading: isPerformanceLoading || isBenchmarkFetching,
  };

  const horizonTranslationMap: Record<Horizon, string> = {
    '1D': t('scenario:filters.horizons.1d'),
    '1W': t('scenario:filters.horizons.1w'),
    '1M': t('scenario:filters.horizons.1m'),
    '3M': t('scenario:filters.horizons.3m'),
    '6M': t('scenario:filters.horizons.6m'),
    '1Y': t('scenario:filters.horizons.1y'),
    ...horizonLabelOverrides,
  };

  return (
    <div
      className={clsx(styles.horizonWrapper, {
        [styles.selected]: isSelected,
      })}
      onClick={onClick}
    >
      <span className={styles.horizonTitle}>
        {horizonTranslationMap[horizon]}
      </span>
      <span className={styles.secondRowWrapper}>
        {benchmarkTags && (
          <span className={styles.horizonPrefix}>
            {t(
              `widget:themeOverview.tabs.overview.${
                dataToUse.isNegative ? 'horizonPrefixUp' : 'horizonPrefixOp'
              }`
            )}
          </span>
        )}
        {dataToUse.isLoading ? (
          <div className={styles.skeletonLoaderWrapper}>
            <SkeletonLoader
              areas={[
                {
                  styles: {
                    width: '50px',
                    height: '20px',
                    borderRadius: '8px',
                  },
                },
              ]}
            />
          </div>
        ) : (
          <span
            className={clsx(styles.horizonValue, {
              [styles.negative]: dataToUse.isNegative,
            })}
          >
            {dataToUse.value}
          </span>
        )}
      </span>
    </div>
  );
};
